import React, { useEffect, useState } from "react";
import { DownloadSimple, X, DeviceMobile, Desktop, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * PWA install helper.
 *   - Listens for the `beforeinstallprompt` event (Chrome / Edge / Android).
 *   - Detects iOS Safari, where install is manual (Share → Add to Home Screen).
 *   - Renders a floating suggestion card the first time it can install.
 *   - Also exposes a button that always shows if install is possible / available.
 */

const DISMISS_KEY = "rcn_install_dismissed_at";

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [visible, setVisible] = useState(false);
  const isIos =
    typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !window.MSStream;
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  useEffect(() => {
    if (isStandalone) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      // Show again after 3 days
      if (Date.now() - dismissedAt > 3 * 24 * 60 * 60 * 1000) {
        setVisible(true);
      }
    };
    const onInstalled = () => setInstalled(true);

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS: no beforeinstallprompt — suggest manual install once if not dismissed
    if (isIos && !isStandalone) {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (Date.now() - dismissedAt > 3 * 24 * 60 * 60 * 1000) {
        setVisible(true);
      }
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const doInstall = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch (err) {
      console.debug("[InstallPrompt] prompt failed", err);
    }
    setDeferred(null);
    setVisible(false);
  };

  if (isStandalone || installed) return null;
  if (!visible) return null;
  if (!deferred && !isIos) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-96 z-50 fade-up"
    >
      <div className="bg-[#111111] border border-[#0055FF]/40 rounded-sm shadow-2xl shadow-black/50 overflow-hidden">
        <div className="h-1 bg-[#0055FF]" />
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#0055FF] flex items-center justify-center shrink-0">
              <DownloadSimple size={20} weight="fill" color="#fff" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base text-white leading-tight">
                Instala Warehouse RCN
              </div>
              <div className="text-xs text-white/60 mt-1">
                {isIos
                  ? "Añádela a tu pantalla de inicio para acceso rápido desde tu iPhone."
                  : "Instálala como app en tu computadora o teléfono para acceso rápido."}
              </div>
            </div>
            <button
              data-testid="install-dismiss"
              onClick={dismiss}
              className="p-1 rounded-sm text-white/40 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          {isIos ? (
            <div className="mt-3 text-[11px] text-white/60 space-y-1 pl-1">
              <div>1. Pulsa el ícono <ArrowSquareOut size={11} className="inline text-[#0055FF]" /> <b className="text-white">Compartir</b>.</div>
              <div>2. Elige <b className="text-white">&quot;Añadir a pantalla de inicio&quot;</b>.</div>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <button
                data-testid="install-confirm"
                onClick={doInstall}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-sm bg-[#0055FF] text-white text-xs font-semibold hover:brightness-110 active:scale-[0.98] transition-transform"
              >
                <DownloadSimple size={14} weight="fill" /> Instalar app
              </button>
              <button
                onClick={dismiss}
                className="px-3 rounded-sm border border-white/10 text-xs text-white/60 hover:bg-white/5 transition-colors"
              >
                Ahora no
              </button>
            </div>
          )}
          <div className="mt-3 flex items-center gap-3 text-[10px] text-white/40 font-mono-data">
            <span className="flex items-center gap-1"><Desktop size={11} /> WINDOWS · MAC</span>
            <span className="flex items-center gap-1"><DeviceMobile size={11} /> ANDROID · IOS</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact "Install app" button for the sidebar / topbar.
 * Only renders when an install is available OR on iOS.
 */
export function InstallButton({ className = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const isIos =
    typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !window.MSStream;
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (isStandalone || installed) return null;
  if (!deferred && !isIos) return null;

  const doInstall = async () => {
    if (deferred) {
      await deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    } else if (isIos) {
      alert(
        "En iPhone/iPad: pulsa el ícono Compartir y luego 'Añadir a pantalla de inicio'."
      );
    }
  };

  return (
    <button
      onClick={doInstall}
      data-testid="install-app-button"
      className={`flex items-center gap-2 px-3 py-2 rounded-sm border border-[#0055FF]/40 bg-[#0055FF]/10 text-xs font-semibold text-[#0055FF] hover:bg-[#0055FF]/20 transition-colors ${className}`}
    >
      <DownloadSimple size={14} weight="fill" /> Instalar app
    </button>
  );
}
